/**
 * Plan of Record Settings — workspace default page layout for new PoR records.
 */
import { useEffect } from 'react';
import { BookOpen } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { PorPageLayoutForm } from '../../../components/plan-of-record/PorPageLayoutForm';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { POR_PAGE_REGISTRY, type PorPageKey } from '../../../lib/porPageSettingsService';
import { usePorPageSettingsStore } from '../../../stores/prioritization/porPageSettingsStore';

export default function PlanOfRecordSettings() {
  const workspaceId = getCurrentWorkspaceId();
  const fetchWorkspaceSettings = usePorPageSettingsStore((s) => s.fetchWorkspaceSettings);
  const setMode = usePorPageSettingsStore((s) => s.setMode);
  // Must subscribe to the rows (not only store methods) or checkbox toggles never re-render.
  const workspaceSettings = usePorPageSettingsStore((s) => s.workspaceSettings);
  const toggleVisible = usePorPageSettingsStore((s) => s.toggleVisible);
  const loading = usePorPageSettingsStore((s) => s.loading);
  const error = usePorPageSettingsStore((s) => s.error);

  useEffect(() => {
    setMode('workspace');
    if (workspaceId) void fetchWorkspaceSettings(workspaceId);
  }, [workspaceId, fetchWorkspaceSettings, setMode]);

  const pageIsVisible = (key: PorPageKey) =>
    workspaceSettings.find((r) => r.pageKey === key)?.isVisible !== false;
  const onlyOneVisible = POR_PAGE_REGISTRY.filter((p) => pageIsVisible(p.key)).length <= 1;

  return (
    <div className="space-y-8">
      <PageHeader
        icon={<BookOpen className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Plan of Record Settings"
        subtitle="Workspace default layout — copied to each new Plan of Record at creation. Customize individual records from Page layout inside that record."
      />

      {!workspaceId && (
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
          <p className="text-sm text-[var(--mustard)]">Select a workspace to configure Plan of Record pages.</p>
        </div>
      )}

      {error && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <p className="text-sm text-[var(--coral)]">{error}</p>
        </div>
      )}

      <div className="card bg-[var(--slate-dim)] border-[var(--slate)] space-y-2">
        <p className="text-sm font-medium text-[var(--text-sec)]">How this works</p>
        <p className="text-sm text-[var(--text-sec)]">
          These checkboxes set the <strong>default</strong> page layout for new Plans of Record.
          Existing records keep their own layout unless you reset them. Open any Plan of Record →
          <strong> Page layout</strong> to customize that record independently.
        </p>
      </div>

      {workspaceId && (
        loading ? (
          <div className="card text-sm text-[var(--text-ter)] py-6 text-center">Loading page settings…</div>
        ) : (
          <PorPageLayoutForm
            isVisible={(key) => pageIsVisible(key as PorPageKey)}
            onlyOneVisible={onlyOneVisible}
            onToggle={(key) => {
              if (onlyOneVisible && pageIsVisible(key as PorPageKey)) return;
              void toggleVisible(key as PorPageKey, workspaceId);
            }}
          />
        )
      )}
    </div>
  );
}
